import { Component, Input, Output, EventEmitter, OnChanges, ElementRef, ViewChild, SimpleChanges } from '@angular/core';
import { GridData, GridCell, GridHighlightLocation, GridExtractionService } from '../../services/grid-extraction.service';
import { LoggerService } from 'src/app/handlers/logger.service';

enum ColumnDataCharacteristic {
  Currency = 'currency data',
  NumbersOnly = 'numbers only',
  Alphanumeric = 'alphanumeric',
  Unknown = 'unknown'
}

interface ColumnSummary {
  index: number;
  header: string;
  averageLength: number;
  dataCharacteristic: ColumnDataCharacteristic;
  averageCurrencyValue: number | null;
}

@Component({
  selector: 'app-grid-extraction-panel',
  standalone: false,
  templateUrl: './grid-extraction-panel.component.html',
  styleUrls: ['./grid-extraction-panel.component.scss']
})
export class GridExtractionPanelComponent implements OnChanges {
  @Input() gridData: GridData | null = null;
  @Input() lineItemColumns: any[] = [];
  @Input() pageKey: string | null = null;
  @Output() rowIgnoreChange = new EventEmitter<{ rowIndex: number; ignored: boolean }>();
  @Output() cellTextChange = new EventEmitter<{ cell: GridCell; newText: string }>();
  @Output() importData = new EventEmitter<any>();
  @Output() cellClick = new EventEmitter<{ cell: GridCell; location: GridHighlightLocation }>();

  @ViewChild('gridPanel', { static: false }) gridPanel!: ElementRef;

  lineItemColumnOptions: any[] = [];
  columnMappings: { [key: number]: string } = {};
  displayRows: any[] = [];
  displayColumns: any[] = [];
  private columnSummaries: ColumnSummary[] = [];
  private hasHeaderRow = false;
  private readonly UNMAPPED_FIELD = '__GRID_COLUMN_UNMAPPED__';
  private readonly UNMAPPED_LABEL = '-';
  private readonly GROSS_TOTAL_EXCLUSION_KEYWORDS = ['net', 'ex', 'unit'];
  private readonly NET_TOTAL_EXCLUSION_KEYWORDS = ['gross', 'inc', 'unit'];
  private readonly QUANTITY_HEADER_KEYWORDS = ['quantity', 'qty', 'receive', 'recieve'];
  private readonly SUPPLIER_REFERENCE_HEADER_KEYWORDS = ['id', 'code', 'ref'];

  constructor(
    private logger: LoggerService,
    private gridExtractionService: GridExtractionService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gridData']) {
      this.hasHeaderRow = this.detectHeadersInFirstRow();

      if (this.gridData) {
        this.refreshDataTable();
        const templateApplied = this.applySavedColumnMappingsIfAvailable();
        if (!templateApplied) {
          this.autoMapColumns(this.columnSummaries);
        }
        this.logGridDataConfidence();
      } else {
        this.displayColumns = [];
        this.displayRows = [];
      }
    }

    if (changes['lineItemColumns']) {
      this.buildLineItemColumnOptions();
    }
  }

  // ======================== Data table load
  private refreshDataTable(): void {
    // Prepare columns for display
    this.displayColumns = [];
    this.columnMappings = {};
    for (let col = 0; col < this.gridData.cols; col++) {
      let header = `Column ${col + 1}`; // Default header

      if (this.hasHeaderRow) {
        // Use first row content as header if detected
        const firstRowCell = this.gridData.cells.find(c => c.row === 0 && c.col === col);
        if (firstRowCell && firstRowCell.text.trim()) {
          header = firstRowCell.text.trim();
        }
      }

      this.displayColumns.push({
        field: `col${col}`,
        header: header,
        colIndex: col
      });
      this.columnMappings[col] = this.UNMAPPED_FIELD;
    }

    // Prepare rows for display (skip first row if it's headers)
    this.displayRows = [];
    const startRow = this.hasHeaderRow ? 1 : 0;

    for (let row = startRow; row < this.gridData.rows; row++) {
      const rowData: any = { rowIndex: row, ignored: false };

      for (let col = 0; col < this.gridData.cols; col++) {
        const cell = this.gridData.cells.find(c => c.row === row && c.col === col);
        rowData[`col${col}`] = cell || { row, col, text: '', ignored: false, confidence: 0, hasLowConfidence: false, bounds: {} };
      }

      // Check if entire row is ignored
      const rowCells = this.gridData.cells.filter(c => c.row === row);
      rowData.ignored = rowCells.length > 0 && rowCells.every(c => c.ignored);

      this.displayRows.push(rowData);
    }

    this.columnSummaries = this.buildColumnSummaries();
    this.applyColumnWidths(this.columnSummaries);
  }

  // ======================== Column Automapping
  private autoMapColumns(columnSummaries?: ColumnSummary[]): void {
    if (!this.gridData) {
      return;
    }

    const summaries = columnSummaries && columnSummaries.length > 0
      ? columnSummaries
      : this.columnSummaries.length > 0
        ? this.columnSummaries
        : this.buildColumnSummaries();

    summaries.forEach(summary => {
      console.log('[Grid Extraction Panel] Column summary:', summary);
    });
    this.applyAutoMapping(summaries);
  }

  private buildColumnSummaries(): ColumnSummary[] {
    if (!this.gridData) {
      return [];
    }

    const startRow = this.hasHeaderRow ? 1 : 0;
    const columnSummaries: ColumnSummary[] = [];

    for (let col = 0; col < this.gridData.cols; col++) {
      const headerLabel = this.displayColumns.find(column => column.colIndex === col)?.header || `Column ${col + 1}`;
      const columnCells = this.gridData.cells.filter(cell => cell.col === col && cell.row >= startRow);

      let totalLength = 0;
      let populatedCellCount = 0;
      let currencyMatches = 0;
      let numericMatches = 0;
      let alphanumericMatches = 0;

      columnCells.forEach(cell => {
        const rawText = (cell.text || '').trim();
        if (!rawText) {
          return;
        }

        totalLength += rawText.length;
        populatedCellCount++;

        const sanitizedValue = rawText.replace(/[^0-9a-zA-Z.]/g, '');
        if (!sanitizedValue) {
          return;
        }

        if (/^\d+\.\d{2}$/.test(sanitizedValue)) {
          currencyMatches++;
        } else if (/^\d+$/.test(sanitizedValue)) {
          numericMatches++;
        } else {
          alphanumericMatches++;
        }
      });

      const averageLength = populatedCellCount ? Number((totalLength / populatedCellCount).toFixed(2)) : 0;
      const classificationOptions = [
        { label: ColumnDataCharacteristic.Currency, count: currencyMatches },
        { label: ColumnDataCharacteristic.NumbersOnly, count: numericMatches },
        { label: ColumnDataCharacteristic.Alphanumeric, count: alphanumericMatches }
      ];
      const dominantClassification = classificationOptions.reduce((prev, curr) => curr.count > prev.count ? curr : prev);
      const dataCharacteristic = dominantClassification.count > 0 ? dominantClassification.label : ColumnDataCharacteristic.Unknown;

      let averageCurrencyValue: number | null = null;
      if (dataCharacteristic === ColumnDataCharacteristic.Currency) {
        const numericValues: number[] = [];
        columnCells.forEach(cell => {
          const rawText = (cell.text || '').trim();
          if (!rawText) {
            return;
          }

          const sanitizedValue = rawText.replace(/[^0-9.]/g, '');
          if (!sanitizedValue) {
            return;
          }

          if (/^\d+\.\d{2}$/.test(sanitizedValue)) {
            numericValues.push(parseFloat(sanitizedValue));
          } else if (/^\d+$/.test(sanitizedValue)) {
            if (sanitizedValue.length > 2) {
              const normalizedValue = sanitizedValue.slice(0, -2) + '.' + sanitizedValue.slice(-2);
              numericValues.push(parseFloat(normalizedValue));
            }
          }
        });

        if (numericValues.length > 0) {
          const sum = numericValues.reduce((acc, val) => acc + val, 0);
          averageCurrencyValue = Number((sum / numericValues.length).toFixed(2));
        }
      }

      columnSummaries.push({
        index: col,
        header: headerLabel,
        averageLength,
        dataCharacteristic,
        averageCurrencyValue
      });
    }

    return columnSummaries;
  }

  private applyColumnWidths(columnSummaries: ColumnSummary[]): void {
    if (!columnSummaries.length || !this.displayColumns.length) {
      return;
    }

    columnSummaries.forEach(summary => {
      const targetColumn = this.displayColumns.find(col => col.colIndex === summary.index);
      if (!targetColumn) {
        return;
      }
      targetColumn.widthPx = this.calculateColumnWidth(summary);
    });
  }

  private calculateColumnWidth(summary: ColumnSummary): number {
    // Estimate width using average character length and data type heuristics
    const basePadding = summary.dataCharacteristic === ColumnDataCharacteristic.NumbersOnly ? 30 : 60;
    const charWidth =
      summary.dataCharacteristic === ColumnDataCharacteristic.NumbersOnly
        ? 6
        : summary.dataCharacteristic === ColumnDataCharacteristic.Currency
          ? 7
          : 8;
    const rawWidth = (summary.averageLength || 1) * charWidth + basePadding;
    const minWidth =
      summary.dataCharacteristic === ColumnDataCharacteristic.NumbersOnly ? 80 :
      summary.dataCharacteristic === ColumnDataCharacteristic.Currency ? 120 : 150;
    const maxWidth =
      summary.dataCharacteristic === ColumnDataCharacteristic.Alphanumeric ? 360 : 240;

    return Math.min(maxWidth, Math.max(minWidth, Math.round(rawWidth)));
  }

  private applyAutoMapping(columnSummaries: ColumnSummary[]): void {
    if (!columnSummaries.length) {
      return;
    }

    const assignedColumns = new Set<number>();
    const assignedFields = new Set<string>();
    columnSummaries.forEach(summary => {
      this.columnMappings[summary.index] = this.UNMAPPED_FIELD;
    });

    // this block lets a specific grid extraction service override the panel’s default invoice auto-mapping logic.
    const serviceMappings = this.gridExtractionService.getAutoColumnMappings(columnSummaries, this.lineItemColumns);
    if (serviceMappings) {
      Object.entries(serviceMappings).forEach(([index, field]) => {
        this.columnMappings[Number(index)] = field;
      });
      return;
    }

    const isFieldAvailable = (field: string) => !assignedFields.has(field);
    const isColumnAvailable = (index: number) => this.columnMappings[index] === this.UNMAPPED_FIELD && !assignedColumns.has(index);
    const assignFieldToSummary = (field: string, summary?: ColumnSummary): boolean => {
      if (!summary || !isColumnAvailable(summary.index) || !isFieldAvailable(field)) {
        return false;
      }
      this.columnMappings[summary.index] = field;
      assignedColumns.add(summary.index);
      assignedFields.add(field);
      return true;
    };

    // 1. find description column - column with longest data
    const availableSummaries = columnSummaries.filter(summary => isColumnAvailable(summary.index));
    let descriptionSummary: ColumnSummary | undefined;
    if (availableSummaries.length > 0) {
      const descriptionTarget = availableSummaries.reduce((prev, curr) => curr.averageLength > prev.averageLength ? curr : prev);
      if (assignFieldToSummary('description', descriptionTarget)) {
        descriptionSummary = descriptionTarget;
      }
    }

    // 2. find gross total column - currency column without net, unit indicators
    const currencyColumns = columnSummaries.filter(summary => summary.dataCharacteristic === ColumnDataCharacteristic.Currency);
    const grossCandidates = currencyColumns.filter(summary => isColumnAvailable(summary.index) && !this.headerContainsKeyword(summary.header, this.GROSS_TOTAL_EXCLUSION_KEYWORDS));
    const grossCandidate = grossCandidates.length > 1
      ? grossCandidates.reduce((prev, curr) => {
        if (curr.averageCurrencyValue === null) {
          return prev;
        }
        if (prev.averageCurrencyValue === null || curr.averageCurrencyValue > prev.averageCurrencyValue) {
          return curr;
        }
        return prev;
      })
      : grossCandidates[0];
    const grossAssigned = assignFieldToSummary('grossTotal', grossCandidate);

    // 3. find net total column - currency column without gross, unit indicators
    if (!grossAssigned) {
      const netCandidate = currencyColumns.find(summary => isColumnAvailable(summary.index) && !this.headerContainsKeyword(summary.header, this.NET_TOTAL_EXCLUSION_KEYWORDS));
      assignFieldToSummary('netTotal', netCandidate);
    }

    // 4. find quantity column - before based on keyword, and before last currency column
    const sortedCurrencyColumns = [...currencyColumns].sort((a, b) => a.index - b.index);
    const lastCurrencyColumn = sortedCurrencyColumns.length > 0 ? sortedCurrencyColumns[sortedCurrencyColumns.length - 1] : null;
    if (lastCurrencyColumn) {
      // consider columns before the last currency column for quantity candidates
      const quantityCandidate = columnSummaries
        .filter(summary => summary.index < lastCurrencyColumn.index)
        .find(summary => isColumnAvailable(summary.index) && this.headerContainsKeyword(summary.header, this.QUANTITY_HEADER_KEYWORDS));
      assignFieldToSummary('quantity', quantityCandidate);
    }

    // 5. find supplier reference - non currency, before or immediately after description, keyword for tie breaker
    if (descriptionSummary) {
      const descriptionIndex = descriptionSummary.index;
      const supplierReferenceCandidates = columnSummaries.filter(summary => {
        if (!isColumnAvailable(summary.index)) {
          return false;
        }

        const isAlphanumericOrNumeric = summary.dataCharacteristic === ColumnDataCharacteristic.Alphanumeric
          || summary.dataCharacteristic === ColumnDataCharacteristic.NumbersOnly;
        if (!isAlphanumericOrNumeric) {
          return false;
        }

        return summary.index <= descriptionIndex + 1;
      });

      if (supplierReferenceCandidates.length === 1) {
        assignFieldToSummary('supplierReference', supplierReferenceCandidates[0]);
      } else if (supplierReferenceCandidates.length > 1) {
        const narrowedCandidates = supplierReferenceCandidates.filter(candidate =>
          this.headerContainsKeyword(candidate.header, this.SUPPLIER_REFERENCE_HEADER_KEYWORDS)
        );

        if (narrowedCandidates.length === 1) {
          assignFieldToSummary('supplierReference', narrowedCandidates[0]);
        }
      }
    }
  }

  private headerContainsKeyword(header: string, keywords: string[]): boolean {
    if (!keywords.length) {
      return false;
    }

    const normalized = (header || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
    if (!normalized) {
      return false;
    }

    const words = normalized.split(' ');
    return keywords.some(keyword => words.includes(keyword.toLowerCase()));
  }

  // ======================== Data Import
  onImportAdd(): void {
    this.processImport('add');
  }

  onImportOverwrite(): void {
    this.processImport('overwrite');
  }

  private processImport(mode: 'add' | 'overwrite'): void {
    if (!this.gridData || !this.hasSufficientMappings()) {
      return;
    }

    const dataCells = this.getDataCellsOnly();
    const rowCount = dataCells.length > 0
      ? Math.max(...dataCells.map(c => c.row)) + 1
      : 0;

    const sanitizedMappings = this.getSanitizedColumnMappings();
    this.persistColumnTemplateSnapshot(sanitizedMappings);

    this.importData.emit({
      cells: dataCells,
      columnMappings: sanitizedMappings,
      rows: rowCount,
      cols: this.gridData.cols,
      mode: mode,
      hasHeaders: this.hasHeaderRow,
      ignoredRows: this.getIgnoredRowIndexes()
    });
  }

  hasSufficientMappings(): boolean {
    const mappedFields = new Set<string>();
    Object.values(this.columnMappings).forEach(mapping => {
      if (mapping && mapping !== this.UNMAPPED_FIELD) {
        mappedFields.add(mapping);
      }
    });

    if (mappedFields.size === 0) {
      return false;
    }

    if (this.isStatementMappingMode()) {
      return true;
    }

    const hasProductIdentifier = ['plu', 'supplierReference', 'description'].some(field => mappedFields.has(field));
    const hasQuantity = mappedFields.has('quantity');
    const hasTotal = ['netTotal', 'grossTotal'].some(field => mappedFields.has(field));

    return hasProductIdentifier && hasQuantity && hasTotal;
  }

  isStatementMappingMode(): boolean {
    const availableFields = new Set((this.lineItemColumns || []).map(column => column.field));
    return ['documentDate', 'documentType', 'documentNumber', 'amount'].some(field => availableFields.has(field));
  }

  private getDataCellsOnly(): GridCell[] {
    if (!this.gridData) return [];

    const startRow = this.hasHeaderRow ? 1 : 0; // Skip first row if it contains headers

    return this.gridData.cells.filter(cell => {
      // Include only data rows (not headers) and non-ignored cells
      return cell.row >= startRow && !cell.ignored;
    });
  }

  private getIgnoredRowIndexes(): number[] {
    if (!this.gridData) return [];

    const startRow = this.hasHeaderRow ? 1 : 0;
    const ignoredRows: number[] = [];

    for (let row = startRow; row < this.gridData.rows; row++) {
      const rowCells = this.gridData.cells.filter(cell => cell.row === row);
      const isIgnored = rowCells.length > 0 && rowCells.every(cell => cell.ignored);

      if (isIgnored) {
        ignoredRows.push(row);
      }
    }

    return ignoredRows;
  }

  private getSanitizedColumnMappings(): { [key: number]: string | null } {
    const sanitized: { [key: number]: string | null } = {};

    Object.keys(this.columnMappings).forEach(key => {
      const index = Number(key);
      const mapping = this.columnMappings[index];
      sanitized[index] = mapping === this.UNMAPPED_FIELD ? null : mapping;
    });

    return sanitized;
  }

  private applySavedColumnMappingsIfAvailable(): boolean {
    if (!this.gridData) {
      return false;
    }

    const template = this.gridExtractionService.getColumnTemplateSnapshot();
    if (!template || template.columnCount !== this.gridData.cols) {
      return false;
    }

    Object.keys(template.columnMappings).forEach(key => {
      const index = Number(key);
      const mapping = template.columnMappings[index];
      this.columnMappings[index] = mapping ?? this.UNMAPPED_FIELD;
    });

    return true;
  }

  private persistColumnTemplateSnapshot(sanitizedMappings: { [key: number]: string | null }): void {
    if (!this.gridData) {
      return;
    }

    const columnRatios = this.gridExtractionService.getCurrentColumnRatios();
    this.gridExtractionService.saveColumnTemplateSnapshot(columnRatios, sanitizedMappings, this.gridData.cols);
  }


  // ======================== UI Action/Display helpers
  getDropdownStyleClass(colIndex: number): string {
    return this.columnMappings[colIndex] === this.UNMAPPED_FIELD ? 'unmapped-selection' : '';
  }

  onClose(): void {
    // Emit close event to parent
    this.importData.emit({ action: 'close' });
  }

  onCellEdit(cell: GridCell, newValue: string): void {
    cell.text = newValue;
    if (cell.hasLowConfidence) {
      cell.hasLowConfidence = false;
    }
    this.cellTextChange.emit({ cell, newText: newValue });
  }

  getActiveRowsCount(): number {
    return this.countRowsByIgnoredState(false);
  }

  getIgnoredRowsCount(): number {
    return this.countRowsByIgnoredState(true);
  }

  private countRowsByIgnoredState(ignoredState: boolean): number {
    if (!this.gridData) return 0;

    const startRow = this.hasHeaderRow ? 1 : 0;
    let count = 0;

    for (let row = startRow; row < this.gridData.rows; row++) {
      const rowCells = this.gridData.cells.filter(cell => cell.row === row);
      const isIgnored = rowCells.length > 0 && rowCells.every(cell => cell.ignored);

      if (ignoredState ? isIgnored : !isIgnored) {
        count++;
      }
    }

    return count;
  }

  toggleRowIgnore(rowIndex: number): void {
    if (!this.gridData) return;

    const rowCells = this.gridData.cells.filter(cell => cell.row === rowIndex);
    const isCurrentlyIgnored = rowCells.length > 0 && rowCells.every(c => c.ignored);
    const nextIgnoredState = !isCurrentlyIgnored;

    // Toggle: if ignored, include; if not ignored, ignore
    rowCells.forEach(cell => cell.ignored = nextIgnoredState);
    this.updateDisplayRowIgnoredState(rowIndex, nextIgnoredState);

    this.rowIgnoreChange.emit({ rowIndex, ignored: nextIgnoredState });
  }

  private updateDisplayRowIgnoredState(rowIndex: number, ignored: boolean): void {
    const displayRow = this.displayRows.find(row => row.rowIndex === rowIndex);
    if (displayRow) {
      displayRow.ignored = ignored;
    }
  }

  onCellFocus(cell: GridCell): void {
    const location = cell.location || (this.pageKey ? {
      pageKey: this.pageKey,
      row: cell.row,
      col: cell.col
    } : null);

    if (!location) {
      this.logger.warn('[Grid Panel] Cannot highlight cell: missing location and page key.');
      return;
    }

    this.cellClick.emit({ cell, location });
  }

  onCellKeydown(event: KeyboardEvent, rowIndex: number, colIndex: number): void {
    if (!this.gridData) return;

    if (event.key === 'ArrowUp') {
      this.moveFocusToRow(event, rowIndex, colIndex, -1);
    } else if (event.key === 'ArrowDown') {
      this.moveFocusToRow(event, rowIndex, colIndex, 1);
    }
  }

  private moveFocusToRow(event: KeyboardEvent, rowIndex: number, colIndex: number, direction: 1 | -1): void {
    const targetRow = this.findNextRowIndex(rowIndex, direction);
    if (targetRow !== null) {
      event.preventDefault();
      this.focusCellInput(targetRow, colIndex);
    }
  }

  private findNextRowIndex(currentRow: number, direction: 1 | -1): number | null {
    if (!this.gridData) return null;

    const startRow = this.hasHeaderRow ? 1 : 0;
    let nextRow = currentRow + direction;

    while (nextRow >= startRow && nextRow < this.gridData.rows) {
      const rowData = this.displayRows.find(row => row.rowIndex === nextRow);
      if (rowData && !rowData.ignored) {
        return nextRow;
      }
      nextRow += direction;
    }

    return null;
  }

  private focusCellInput(rowIndex: number, colIndex: number): boolean {
    if (!this.gridPanel) return false;

    const selector = `.cell-input[data-row="${rowIndex}"][data-col="${colIndex}"]`;
    const input: HTMLInputElement | null = this.gridPanel.nativeElement.querySelector(selector);

    if (input && !input.disabled) {
      input.focus();
      input.select();
      return true;
    }

    return false;
  }

  // ======================== Util method

  private detectHeadersInFirstRow(): boolean {
    if (!this.gridData || this.gridData.rows < 2) return false;

    const headerKeywords = this.gridExtractionService.getHeaderKeywords();
    // Get first row cells
    const firstRowCells = this.gridData.cells.filter(c => c.row === 0);
    if (firstRowCells.length === 0) return false;

    // Count how many cells match header keywords
    let matchCount = 0;
    firstRowCells.forEach(cell => {
      const cellText = (cell.text || '').toLowerCase().trim();
      if (cellText) {
        const hasMatch = headerKeywords.some(keyword => cellText.includes(keyword.toLowerCase()));
        if (hasMatch) {
          matchCount++;
        }
      }
    });

    // Require at least 2 matches to consider it a header row
    return matchCount >= 2;
  }

  private logGridDataConfidence(): void {
    if (!this.gridData) {
      return;
    }

    const tableData = [];
    for (let row = 0; row < this.gridData.rows; row++) {
      const rowEntry: Record<string, string | number> = { Row: row };

      for (let col = 0; col < this.gridData.cols; col++) {
        const cell = this.gridData.cells.find(c => c.row === row && c.col === col);
        const cellText = (cell?.text ?? '').trim();
        const confidence = cell?.confidence ?? 'N/A';
        rowEntry[`Column ${col + 1}`] = `${cellText} (${confidence})`;
      }

      tableData.push(rowEntry);
    }

    console.log('[Grid Extraction Panel] Populated grid data with confidence details:');
    console.table(tableData);
  }

  private buildLineItemColumnOptions(): void {
    this.lineItemColumnOptions = [
      { header: this.UNMAPPED_LABEL, field: this.UNMAPPED_FIELD },
      ...(this.lineItemColumns || []).map(({ header, field }) => ({ header, field }))
    ];
  }

}
